import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Alert, Pagination, Stack, CircularProgress, Grid, Card, CardContent,
  Avatar, Divider, alpha, IconButton, Tabs, Tab, Tooltip,
} from '@mui/material';
import {
  Description, Draw, Cancel, CheckCircle, Timer, Warning, Gavel,
  CalendarMonth, AttachMoney, Person, Business, AutoAwesome, Refresh,
  Visibility, Download, Print, Add, NoteAdd, Edit, Save,
} from '@mui/icons-material';
import useAuthStore from '../../store/useStore';
import { tenancyAPI } from '../../api/client';
import { integerOnly, decimalOnly } from '../../utils/inputHelpers';

const statusColors = {
  draft: 'default', pending_tenant: 'warning', pending_landlord: 'info',
  active: 'success', expired: 'error', terminated: 'error', disputed: 'warning',
};

const statusIcons = {
  draft: <Description />, pending_tenant: <Timer />, pending_landlord: <Timer />,
  active: <CheckCircle />, expired: <Warning />, terminated: <Cancel />, disputed: <Gavel />,
};

function TenancyPage() {
  const { user } = useAuthStore();
  const [agreements, setAgreements] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [terminateDialog, setTerminateDialog] = useState(null);
  const [terminateReason, setTerminateReason] = useState('');
  const [generateLoading, setGenerateLoading] = useState(false);
  const [tabValue, setTabValue] = useState(0);
  const [createOpen, setCreateOpen] = useState(false);
  const [availableBookings, setAvailableBookings] = useState([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [createForm, setCreateForm] = useState({
    monthly_rent: '', deposit_amount: '', payment_due_day: 1,
    notice_period_days: 30, terms_and_conditions: '', special_clauses: '',
  });
  const isLandlord = ['landlord', 'agent'].includes(user?.role);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ terms_and_conditions: '', special_clauses: '', notice_period_days: 30 });
  const [editLoading, setEditLoading] = useState(false);

  const handleStartEdit = () => {
    if (!selectedAgreement) return;
    setEditForm({
      terms_and_conditions: selectedAgreement.terms_and_conditions || '',
      special_clauses: selectedAgreement.special_clauses || '',
      notice_period_days: selectedAgreement.notice_period_days || 30,
    });
    setEditMode(true);
  };

  const handleSaveEdit = async () => {
    if (!selectedAgreement) return;
    setEditLoading(true);
    try {
      await tenancyAPI.update(selectedAgreement.id, editForm);
      setMessage({ type: 'success', text: 'Agreement updated successfully!' });
      setEditMode(false);
      const updated = await tenancyAPI.getById(selectedAgreement.id);
      setSelectedAgreement(updated.data);
      fetchAgreements();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to update agreement' });
    } finally {
      setEditLoading(false);
    }
  };

  const renderRichText = (text) => {
    if (!text) return null;
    const lines = text.split('\n');
    return lines.map((line, i) => {
      const trimmed = line.trim();
      if (!trimmed) return <Box key={i} sx={{ height: 8 }} />;
      // Bold section headers: lines starting with ## or all-caps lines ending with colon
      if (trimmed.startsWith('## ')) {
        return <Typography key={i} variant="subtitle2" fontWeight={800} sx={{ mt: 2, mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, color: '#1e293b', fontFamily: '"Georgia", serif' }}>{trimmed.replace('## ', '')}</Typography>;
      }
      if (/^[A-Z][A-Z\s&]+:?$/.test(trimmed) || /^\d+\.\s+[A-Z]/.test(trimmed)) {
        return <Typography key={i} variant="subtitle2" fontWeight={800} sx={{ mt: 1.5, mb: 0.5, color: '#1e293b', fontFamily: '"Georgia", serif' }}>{trimmed}</Typography>;
      }
      // Bullet points: lines starting with - or * or •
      if (/^[-*•]\s/.test(trimmed)) {
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', ml: 2, mb: 0.3 }}>
            <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#64748b', mt: 1, mr: 1.5, flexShrink: 0 }} />
            <Typography variant="body2" sx={{ lineHeight: 1.8, fontFamily: '"Georgia", serif', fontSize: '0.85rem' }}>{trimmed.replace(/^[-*•]\s/, '')}</Typography>
          </Box>
        );
      }
      // Numbered items
      if (/^\d+\.\s/.test(trimmed)) {
        const num = trimmed.match(/^(\d+)\.\s/)[1];
        return (
          <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', ml: 2, mb: 0.3 }}>
            <Typography variant="body2" sx={{ fontWeight: 700, mr: 1, minWidth: 18, fontFamily: '"Georgia", serif', fontSize: '0.85rem' }}>{num}.</Typography>
            <Typography variant="body2" sx={{ lineHeight: 1.8, fontFamily: '"Georgia", serif', fontSize: '0.85rem' }}>{trimmed.replace(/^\d+\.\s/, '')}</Typography>
          </Box>
        );
      }
      // Regular paragraph
      return <Typography key={i} variant="body2" sx={{ lineHeight: 1.9, fontFamily: '"Georgia", serif', fontSize: '0.85rem', mb: 0.3 }}>{trimmed}</Typography>;
    });
  };

  const fetchAgreements = async () => {
    setLoading(true);
    try {
      const res = await tenancyAPI.list({ page, per_page: 10 });
      setAgreements(res.data.agreements || []);
      setTotal(res.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch tenancy agreements:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenCreate = async () => {
    setCreateOpen(true);
    try {
      const res = await tenancyAPI.bookingsWithoutAgreement();
      setAvailableBookings(res.data.bookings || []);
    } catch (_) { setAvailableBookings([]); }
  };

  const handleSelectBooking = (booking) => {
    setSelectedBooking(booking);
    setCreateForm(f => ({
      ...f,
      monthly_rent: booking.price_per_month || (booking.total_amount ? Math.round(booking.total_amount / 12) : ''),
      deposit_amount: booking.total_amount ? Math.round(booking.total_amount * 0.1) : '',
    }));
  };

  const handleCreateAgreement = async () => {
    if (!selectedBooking) return;
    setCreateLoading(true);
    try {
      await tenancyAPI.create({
        booking_id: selectedBooking.id,
        start_date: selectedBooking.check_in_date,
        end_date: selectedBooking.check_out_date,
        monthly_rent: Number(createForm.monthly_rent) || 0,
        deposit_amount: Number(createForm.deposit_amount) || 0,
        payment_due_day: Number(createForm.payment_due_day) || 1,
        notice_period_days: Number(createForm.notice_period_days) || 30,
        terms_and_conditions: createForm.terms_and_conditions || null,
        special_clauses: createForm.special_clauses || null,
      });
      setMessage({ type: 'success', text: 'Tenancy agreement created and sent to tenant for signing!' });
      setCreateOpen(false);
      setSelectedBooking(null);
      setCreateForm({ monthly_rent: '', deposit_amount: '', payment_due_day: 1, notice_period_days: 30, terms_and_conditions: '', special_clauses: '' });
      fetchAgreements();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to create agreement' });
    } finally {
      setCreateLoading(false);
    }
  };

  useEffect(() => { fetchAgreements(); }, [page]);

  const handleSign = async (id) => {
    setActionLoading(true);
    try {
      await tenancyAPI.sign(id);
      setMessage({ type: 'success', text: 'Agreement signed successfully! Both parties must sign for the agreement to become active.' });
      fetchAgreements();
      if (selectedAgreement?.id === id) {
        try {
          const res = await tenancyAPI.getById(id);
          setSelectedAgreement(res.data);
        } catch (_) { setDetailOpen(false); }
      }
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to sign' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleTerminate = async () => {
    if (!terminateDialog) return;
    setActionLoading(true);
    try {
      await tenancyAPI.terminate(terminateDialog, terminateReason || 'User requested termination');
      setMessage({ type: 'success', text: 'Agreement terminated successfully' });
      setTerminateDialog(null);
      setTerminateReason('');
      fetchAgreements();
      setDetailOpen(false);
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to terminate' });
    } finally {
      setActionLoading(false);
    }
  };

  const handleGenerateTerms = async (id) => {
    setGenerateLoading(true);
    try {
      const res = await tenancyAPI.generateTerms(id);
      setMessage({ type: 'success', text: 'Terms generated successfully!' });
      if (selectedAgreement?.id === id) {
        try {
          const updated = await tenancyAPI.getById(id);
          setSelectedAgreement(updated.data);
        } catch (_) {}
      }
      fetchAgreements();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.detail || 'Failed to generate terms. AI service may be unavailable.' });
    } finally {
      setGenerateLoading(false);
    }
  };

  const viewDetail = async (agreement) => {
    try {
      const res = await tenancyAPI.getById(agreement.id);
      setSelectedAgreement(res.data);
    } catch (_) {
      setSelectedAgreement(agreement);
    }
    setDetailOpen(true);
  };

  const canSign = (agreement) => {
    if (!agreement) return false;
    // Tenant signs first (when status is pending_tenant or draft)
    if (user?.role === 'tenant' && !agreement.tenant_signed && (agreement.status === 'pending_tenant' || agreement.status === 'draft')) return true;
    // Landlord signs only AFTER tenant has signed (when status is pending_landlord)
    if (['landlord', 'agent'].includes(user?.role) && !agreement.landlord_signed && agreement.status === 'pending_landlord') return true;
    return false;
  };

  const canTerminate = (agreement) => {
    if (!agreement) return false;
    return ['active', 'pending_tenant', 'pending_landlord'].includes(agreement.status);
  };

  const activeCount = agreements.filter(a => a.status === 'active').length;
  const pendingCount = agreements.filter(a => a.status?.includes('pending')).length;

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" fontWeight={800} sx={{ background: 'linear-gradient(135deg, #8b5cf6, #a78bfa)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
            Tenancy Agreements
          </Typography>
          <Typography variant="body2" color="text.secondary">View, sign, and manage your tenancy agreements</Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          {isLandlord && (
            <Button variant="contained" startIcon={<NoteAdd />} onClick={handleOpenCreate}
              sx={{ borderRadius: 2 }}>Create Agreement</Button>
          )}
          <IconButton onClick={fetchAgreements} sx={{ bgcolor: 'white', boxShadow: 1 }}><Refresh /></IconButton>
        </Stack>
      </Box>

      {message.text && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage({ type: '', text: '' })}>{message.text}</Alert>}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Total Agreements', count: total, color: '#6366f1', icon: <Gavel /> },
          { label: 'Active', count: activeCount, color: '#10b981', icon: <CheckCircle /> },
          { label: 'Pending Signature', count: pendingCount, color: '#f59e0b', icon: <Timer /> },
          { label: 'Expired / Terminated', count: agreements.filter(a => ['expired', 'terminated'].includes(a.status)).length, color: '#ef4444', icon: <Cancel /> },
        ].map((item, i) => (
          <Grid item xs={6} sm={3} key={i}>
            <Card sx={{ borderLeft: `4px solid ${item.color}` }}>
              <CardContent sx={{ py: 2 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Avatar sx={{ bgcolor: alpha(item.color, 0.1), color: item.color, width: 36, height: 36 }}>{item.icon}</Avatar>
                  <Box>
                    <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                    <Typography variant="h5" fontWeight={800}>{item.count}</Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : agreements.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center', borderRadius: 3 }}>
          <Description sx={{ fontSize: 64, color: 'grey.300', mb: 2 }} />
          <Typography variant="h6" fontWeight={600}>No tenancy agreements yet</Typography>
          <Typography variant="body2" color="text.secondary">
            Agreements will appear here once a booking is confirmed. The landlord or system will create the agreement automatically.
          </Typography>
        </Paper>
      ) : (
        <>
          <TableContainer component={Paper} sx={{ borderRadius: 3 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell><strong>Agreement #</strong></TableCell>
                  <TableCell><strong>Period</strong></TableCell>
                  <TableCell><strong>Rent/Month</strong></TableCell>
                  <TableCell><strong>Deposit</strong></TableCell>
                  <TableCell><strong>Status</strong></TableCell>
                  <TableCell><strong>Signatures</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agreements.map((a) => (
                  <TableRow key={a.id} hover sx={{ cursor: 'pointer' }} onClick={() => viewDetail(a)}>
                    <TableCell>
                      <Typography fontWeight={600} variant="body2">{a.agreement_number}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {new Date(a.start_date).toLocaleDateString()} - {new Date(a.end_date).toLocaleDateString()}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography fontWeight={600} variant="body2">GH₵ {a.monthly_rent?.toLocaleString()}</Typography></TableCell>
                    <TableCell><Typography variant="body2" color="text.secondary">GH₵ {a.deposit_amount?.toLocaleString()}</Typography></TableCell>
                    <TableCell>
                      <Chip label={a.status.replace('_', ' ')} size="small" color={statusColors[a.status] || 'default'} sx={{ textTransform: 'capitalize' }} />
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5}>
                        <Tooltip title={a.tenant_signed ? `Signed ${a.tenant_signed_at ? new Date(a.tenant_signed_at).toLocaleDateString() : ''}` : 'Not signed'}>
                          <Chip label={a.tenant_signed ? 'Tenant ✓' : 'Tenant ✗'} size="small" variant={a.tenant_signed ? 'filled' : 'outlined'} color={a.tenant_signed ? 'success' : 'default'} />
                        </Tooltip>
                        <Tooltip title={a.landlord_signed ? `Signed ${a.landlord_signed_at ? new Date(a.landlord_signed_at).toLocaleDateString() : ''}` : 'Not signed'}>
                          <Chip label={a.landlord_signed ? 'Landlord ✓' : 'Landlord ✗'} size="small" variant={a.landlord_signed ? 'filled' : 'outlined'} color={a.landlord_signed ? 'success' : 'default'} />
                        </Tooltip>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
                        <Button size="small" variant="outlined" startIcon={<Visibility />} onClick={() => viewDetail(a)}>View</Button>
                        {canSign(a) && (
                          <Button size="small" variant="contained" color="success" startIcon={<Draw />}
                            onClick={() => handleSign(a.id)} disabled={actionLoading}>Sign</Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          {total > 10 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination count={Math.ceil(total / 10)} page={page} onChange={(_, v) => setPage(v)} color="primary" />
            </Box>
          )}
        </>
      )}

      {/* Agreement Detail Dialog — Professional Document */}
      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} maxWidth="md" fullWidth
        PaperProps={{ sx: { borderRadius: 2, overflow: 'hidden' } }}>
        {selectedAgreement && (
          <>
            {/* Professional Header Bar */}
            <Box sx={{ bgcolor: '#1e293b', color: 'white', px: 4, py: 2.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Box>
                <Typography variant="overline" sx={{ letterSpacing: 2, opacity: 0.7 }}>TENANCY AGREEMENT</Typography>
                <Typography variant="h5" fontWeight={800} sx={{ mt: -0.5 }}>CampusNest</Typography>
              </Box>
              <Box sx={{ textAlign: 'right' }}>
                <Typography variant="body2" sx={{ opacity: 0.8 }}>{selectedAgreement.agreement_number}</Typography>
                <Chip label={selectedAgreement.status.replace('_', ' ')} size="small"
                  color={statusColors[selectedAgreement.status] || 'default'}
                  icon={statusIcons[selectedAgreement.status]}
                  sx={{ textTransform: 'capitalize', mt: 0.5, color: 'white', '& .MuiChip-icon': { color: 'inherit' } }} />
              </Box>
            </Box>

            <DialogContent sx={{ p: 0 }}>
              {/* Document Body */}
              <Box sx={{ px: 4, py: 3, fontFamily: '"Georgia", serif' }}>

                {/* Preamble */}
                <Typography variant="body2" sx={{ lineHeight: 1.9, mb: 3, color: 'text.secondary' }}>
                  This Tenancy Agreement ("Agreement") is entered into as of{' '}
                  <strong>{new Date(selectedAgreement.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>,
                  by and between the Landlord/Property Manager and the Tenant, under the terms administered by the CampusNest platform.
                </Typography>

                <Divider sx={{ mb: 3 }} />

                {/* Section 1: Parties */}
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5, fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.85rem', color: '#1e293b' }}>
                  Section 1 — Parties to the Agreement
                </Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #6366f1', bgcolor: alpha('#6366f1', 0.01) }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha('#6366f1', 0.1), color: '#6366f1', width: 44, height: 44 }}><Business /></Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.6rem' }}>Landlord / Property Manager</Typography>
                          <Typography fontWeight={800} sx={{ fontSize: '1rem' }}>{selectedAgreement.landlord_name || 'Property Owner'}</Typography>
                          {selectedAgreement.landlord_company && <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{selectedAgreement.landlord_company}</Typography>}
                          {selectedAgreement.landlord_address && selectedAgreement.landlord_address !== 'N/A' && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>📍 {selectedAgreement.landlord_address}</Typography>
                          )}
                          {selectedAgreement.landlord_email && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>✉ {selectedAgreement.landlord_email}</Typography>}
                          {selectedAgreement.landlord_phone && selectedAgreement.landlord_phone !== 'N/A' && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>☎ {selectedAgreement.landlord_phone}</Typography>}
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, borderLeft: '4px solid #10b981', bgcolor: alpha('#10b981', 0.01) }}>
                      <Stack direction="row" alignItems="flex-start" spacing={1.5}>
                        <Avatar sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', width: 44, height: 44 }}><Person /></Avatar>
                        <Box sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.6rem' }}>Tenant</Typography>
                          <Typography fontWeight={800} sx={{ fontSize: '1rem' }}>{selectedAgreement.tenant_name || 'Tenant'}</Typography>
                          {selectedAgreement.tenant_university && <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>{selectedAgreement.tenant_university}{selectedAgreement.tenant_student_id ? ` (${selectedAgreement.tenant_student_id})` : ''}</Typography>}
                          {selectedAgreement.tenant_address && selectedAgreement.tenant_address !== 'N/A' && (
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.8rem' }}>📍 {selectedAgreement.tenant_address}</Typography>
                          )}
                          {selectedAgreement.tenant_email && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>✉ {selectedAgreement.tenant_email}</Typography>}
                          {selectedAgreement.tenant_phone && selectedAgreement.tenant_phone !== 'N/A' && <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>☎ {selectedAgreement.tenant_phone}</Typography>}
                        </Box>
                      </Stack>
                    </Paper>
                  </Grid>
                </Grid>

                {/* Property Details */}
                {(selectedAgreement.property_title || selectedAgreement.property_address) && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 3, borderLeft: '4px solid #f59e0b', bgcolor: alpha('#f59e0b', 0.01) }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.6rem' }}>Property / Premises</Typography>
                    <Typography fontWeight={700}>{selectedAgreement.property_title}</Typography>
                    {selectedAgreement.property_address && <Typography variant="body2" color="text.secondary">📍 {selectedAgreement.property_address}{selectedAgreement.property_city ? `, ${selectedAgreement.property_city}` : ''}</Typography>}
                    {selectedAgreement.property_type && <Typography variant="body2" color="text.secondary">Type: {selectedAgreement.property_type}</Typography>}
                  </Paper>
                )}

                {/* Section 2: Tenancy Period & Financial Terms */}
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5, fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.85rem', color: '#1e293b' }}>
                  Section 2 — Tenancy Period & Financial Terms
                </Typography>
                <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3 }}>
                  <Grid container spacing={2}>
                    {[
                      { icon: <CalendarMonth sx={{ fontSize: 18, color: '#6366f1' }} />, label: 'Commencement Date', value: new Date(selectedAgreement.start_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) },
                      { icon: <CalendarMonth sx={{ fontSize: 18, color: '#ef4444' }} />, label: 'Expiry Date', value: new Date(selectedAgreement.end_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) },
                      { icon: <AttachMoney sx={{ fontSize: 18, color: '#10b981' }} />, label: 'Monthly Rent', value: `GH\u20B5 ${selectedAgreement.monthly_rent?.toLocaleString()}` },
                      { icon: <AttachMoney sx={{ fontSize: 18, color: '#f59e0b' }} />, label: 'Security Deposit', value: `GH\u20B5 ${selectedAgreement.deposit_amount?.toLocaleString()}` },
                      { label: 'Payment Due Day', value: `${selectedAgreement.payment_due_day || 1}${['st','nd','rd'][((selectedAgreement.payment_due_day||1)-1)%10] && (selectedAgreement.payment_due_day||1) < 4 ? ['st','nd','rd'][(selectedAgreement.payment_due_day||1)-1] : 'th'} of each month` },
                      { label: 'Notice Period', value: `${selectedAgreement.notice_period_days || 30} days written notice` },
                    ].map((item, i) => (
                      <Grid item xs={6} sm={4} key={i}>
                        <Stack direction="row" alignItems="center" spacing={1}>
                          {item.icon && item.icon}
                          <Box>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>{item.label}</Typography>
                            <Typography fontWeight={700} variant="body2">{item.value}</Typography>
                          </Box>
                        </Stack>
                      </Grid>
                    ))}
                  </Grid>
                </Paper>

                {/* Section 3: Terms & Conditions */}
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={800} sx={{ fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.85rem', color: '#1e293b' }}>
                    Section 3 — Terms & Conditions
                  </Typography>
                  <Stack direction="row" spacing={1}>
                    {isLandlord && !editMode && !(selectedAgreement.tenant_signed && selectedAgreement.landlord_signed) && (
                      <Button size="small" variant="outlined" startIcon={<Edit />}
                        onClick={handleStartEdit} sx={{ borderRadius: 2, textTransform: 'none' }}>
                        Edit Agreement
                      </Button>
                    )}
                    {isLandlord && !selectedAgreement.terms_and_conditions && !editMode && (
                      <Button size="small" variant="outlined" startIcon={generateLoading ? <CircularProgress size={14} /> : <AutoAwesome />}
                        onClick={() => handleGenerateTerms(selectedAgreement.id)} disabled={generateLoading}
                        sx={{ borderRadius: 2, textTransform: 'none' }}>
                        {generateLoading ? 'Generating...' : 'Generate with AI'}
                      </Button>
                    )}
                  </Stack>
                </Stack>

                {editMode ? (
                  <Paper variant="outlined" sx={{ p: 3, borderRadius: 2, mb: 3, borderLeft: '3px solid #6366f1' }}>
                    <TextField fullWidth label="Terms & Conditions" multiline rows={8} value={editForm.terms_and_conditions}
                      onChange={(e) => setEditForm({ ...editForm, terms_and_conditions: e.target.value })}
                      helperText="Use ## for section headers, - for bullet points, and numbered lists (1. 2. 3.)" sx={{ mb: 2 }} />
                    <TextField fullWidth label="Special Clauses" multiline rows={3} value={editForm.special_clauses}
                      onChange={(e) => setEditForm({ ...editForm, special_clauses: e.target.value })} sx={{ mb: 2 }} />
                    <TextField label="Notice Period (days)" type="number" value={editForm.notice_period_days}
                      onChange={(e) => setEditForm({ ...editForm, notice_period_days: Number(e.target.value) })} onKeyDown={integerOnly} size="small" sx={{ mb: 2, width: 200 }} />
                    <Stack direction="row" spacing={1}>
                      <Button variant="contained" startIcon={editLoading ? <CircularProgress size={14} /> : <Save />}
                        onClick={handleSaveEdit} disabled={editLoading} sx={{ borderRadius: 2 }}>
                        {editLoading ? 'Saving...' : 'Save Changes'}
                      </Button>
                      <Button variant="outlined" onClick={() => setEditMode(false)} sx={{ borderRadius: 2 }}>Cancel</Button>
                    </Stack>
                  </Paper>
                ) : selectedAgreement.terms_and_conditions ? (
                  <Paper variant="outlined" sx={{ p: 3, maxHeight: 350, overflow: 'auto', borderRadius: 2, bgcolor: '#fafafa', mb: 3, borderLeft: '3px solid #cbd5e1' }}>
                    {renderRichText(selectedAgreement.terms_and_conditions)}
                  </Paper>
                ) : (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2, borderStyle: 'dashed', bgcolor: '#fafafa', mb: 3 }}>
                    <Typography color="text.secondary" variant="body2">
                      No terms have been set yet.
                      {isLandlord
                        ? ' Click "Edit Agreement" or "Generate with AI" to add professional terms.'
                        : ' The landlord will add terms before the agreement can be signed.'}
                    </Typography>
                  </Paper>
                )}

                {/* Section 4: Special Clauses */}
                {selectedAgreement.special_clauses && !editMode && (
                  <>
                    <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 1.5, fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.85rem', color: '#1e293b' }}>
                      Section 4 — Special Clauses
                    </Typography>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, mb: 3, borderLeft: '3px solid #f59e0b' }}>
                      {renderRichText(selectedAgreement.special_clauses)}
                    </Paper>
                  </>
                )}

                {/* Signatures Section */}
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle1" fontWeight={800} sx={{ mb: 2, fontFamily: 'inherit', textTransform: 'uppercase', letterSpacing: 1, fontSize: '0.85rem', color: '#1e293b' }}>
                  Signatures
                </Typography>
                <Grid container spacing={3}>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{
                      p: 2.5, borderRadius: 2, textAlign: 'center', minHeight: 140,
                      borderColor: selectedAgreement.tenant_signed ? 'success.main' : 'divider',
                      bgcolor: selectedAgreement.tenant_signed ? alpha('#10b981', 0.03) : 'transparent',
                      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                    }}>
                      {selectedAgreement.tenant_signed ? (
                        <>
                          <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                          <Typography fontWeight={700} sx={{ fontFamily: '"Brush Script MT", cursive', fontSize: '1.4rem', color: '#1e293b' }}>
                            {selectedAgreement.tenant_name || 'Tenant'}
                          </Typography>
                          <Divider sx={{ width: '60%', my: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">Tenant Signature</Typography>
                          <Typography variant="caption" color="success.main" fontWeight={600}>
                            Signed: {selectedAgreement.tenant_signed_at ? new Date(selectedAgreement.tenant_signed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Person sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                          <Box sx={{ width: '60%', borderBottom: '1px dashed', borderColor: 'grey.400', mb: 0.5, height: 24 }} />
                          <Typography variant="caption" color="text.secondary">Tenant Signature</Typography>
                          <Typography variant="caption" color="warning.main" fontWeight={600}>Awaiting</Typography>
                        </>
                      )}
                    </Paper>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Paper variant="outlined" sx={{
                      p: 2.5, borderRadius: 2, textAlign: 'center', minHeight: 140,
                      borderColor: selectedAgreement.landlord_signed ? 'success.main' : 'divider',
                      bgcolor: selectedAgreement.landlord_signed ? alpha('#10b981', 0.03) : 'transparent',
                      display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center',
                    }}>
                      {selectedAgreement.landlord_signed ? (
                        <>
                          <CheckCircle sx={{ fontSize: 32, color: 'success.main', mb: 1 }} />
                          <Typography fontWeight={700} sx={{ fontFamily: '"Brush Script MT", cursive', fontSize: '1.4rem', color: '#1e293b' }}>
                            {selectedAgreement.landlord_name || 'Landlord'}
                          </Typography>
                          <Divider sx={{ width: '60%', my: 0.5 }} />
                          <Typography variant="caption" color="text.secondary">Landlord Signature</Typography>
                          <Typography variant="caption" color="success.main" fontWeight={600}>
                            Signed: {selectedAgreement.landlord_signed_at ? new Date(selectedAgreement.landlord_signed_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                          </Typography>
                        </>
                      ) : (
                        <>
                          <Business sx={{ fontSize: 32, color: 'grey.400', mb: 1 }} />
                          <Box sx={{ width: '60%', borderBottom: '1px dashed', borderColor: 'grey.400', mb: 0.5, height: 24 }} />
                          <Typography variant="caption" color="text.secondary">Landlord Signature</Typography>
                          <Typography variant="caption" color="warning.main" fontWeight={600}>Awaiting</Typography>
                        </>
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                {/* Document Link */}
                {selectedAgreement.document_url && (
                  <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mt: 3 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <Description color="primary" />
                        <Typography fontWeight={600}>Agreement Document</Typography>
                      </Stack>
                      <Stack direction="row" spacing={1}>
                        <Button size="small" variant="outlined" startIcon={<Download />}
                          href={selectedAgreement.document_url} target="_blank">Download PDF</Button>
                        <Button size="small" variant="outlined" startIcon={<Print />}
                          onClick={() => window.print()}>Print</Button>
                      </Stack>
                    </Stack>
                  </Paper>
                )}

                {/* Footer */}
                <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    This agreement is managed through the CampusNest Student Accommodation Platform.
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Both parties acknowledge that electronic signatures on this platform are legally binding.
                  </Typography>
                </Box>
              </Box>
            </DialogContent>

            <DialogActions sx={{ p: 2.5, bgcolor: '#f8fafc', justifyContent: 'space-between', borderTop: '1px solid', borderColor: 'divider' }}>
              <Box>
                {canTerminate(selectedAgreement) && (
                  <Button color="error" variant="outlined" startIcon={<Cancel />}
                    onClick={() => { setTerminateDialog(selectedAgreement.id); }} sx={{ borderRadius: 2 }}>
                    Terminate Agreement
                  </Button>
                )}
              </Box>
              <Stack direction="row" spacing={1}>
                {canSign(selectedAgreement) && (
                  <Button variant="contained" color="success" startIcon={<Draw />}
                    onClick={() => handleSign(selectedAgreement.id)} disabled={actionLoading}
                    sx={{ px: 4, borderRadius: 2, fontWeight: 700 }}>
                    {actionLoading ? 'Signing...' : 'Sign Agreement'}
                  </Button>
                )}
                <Button onClick={() => setDetailOpen(false)} sx={{ borderRadius: 2 }}>Close</Button>
              </Stack>
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Create Agreement Dialog (Landlord) */}
      <Dialog open={createOpen} onClose={() => { setCreateOpen(false); setSelectedBooking(null); }} maxWidth="md" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Create Tenancy Agreement</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {!selectedBooking ? (
              <>
                <Typography variant="body2" color="text.secondary">
                  Select a confirmed booking to create a tenancy agreement for:
                </Typography>
                {availableBookings.length === 0 ? (
                  <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
                    <Typography color="text.secondary">No confirmed bookings without agreements found.</Typography>
                  </Paper>
                ) : (
                  <Stack spacing={1}>
                    {availableBookings.map((b) => (
                      <Paper key={b.id} variant="outlined" sx={{ p: 2, borderRadius: 2, cursor: 'pointer', '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' } }}
                        onClick={() => handleSelectBooking(b)}>
                        <Stack direction="row" justifyContent="space-between" alignItems="center">
                          <Box>
                            <Typography fontWeight={600}>{b.property_title}</Typography>
                            <Typography variant="body2" color="text.secondary">
                              Tenant: {b.tenant_name} {b.room_number ? `| Room: ${b.room_number}` : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {b.check_in_date ? new Date(b.check_in_date).toLocaleDateString() : ''} - {b.check_out_date ? new Date(b.check_out_date).toLocaleDateString() : ''}
                            </Typography>
                          </Box>
                          <Chip label={`GH\u20B5 ${b.total_amount?.toLocaleString()}`} color="primary" variant="outlined" />
                        </Stack>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </>
            ) : (
              <>
                <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: 'grey.50' }}>
                  <Typography variant="subtitle2" fontWeight={700}>Selected Booking</Typography>
                  <Typography variant="body2">{selectedBooking.property_title} - {selectedBooking.tenant_name}</Typography>
                  <Typography variant="caption" color="text.secondary">
                    {selectedBooking.check_in_date ? new Date(selectedBooking.check_in_date).toLocaleDateString() : ''} - {selectedBooking.check_out_date ? new Date(selectedBooking.check_out_date).toLocaleDateString() : ''}
                  </Typography>
                  <Button size="small" onClick={() => setSelectedBooking(null)} sx={{ ml: 2 }}>Change</Button>
                </Paper>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Monthly Rent (GH\u20B5)" type="number" value={createForm.monthly_rent}
                      onChange={(e) => setCreateForm({ ...createForm, monthly_rent: e.target.value })} onKeyDown={decimalOnly} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Deposit Amount (GH\u20B5)" type="number" value={createForm.deposit_amount}
                      onChange={(e) => setCreateForm({ ...createForm, deposit_amount: e.target.value })} onKeyDown={decimalOnly} />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Payment Due Day" type="number" value={createForm.payment_due_day}
                      onChange={(e) => setCreateForm({ ...createForm, payment_due_day: e.target.value })}
                      onKeyDown={integerOnly} inputProps={{ min: 1, max: 28 }} helperText="Day of month rent is due" />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField fullWidth label="Notice Period (days)" type="number" value={createForm.notice_period_days}
                      onChange={(e) => setCreateForm({ ...createForm, notice_period_days: e.target.value })} onKeyDown={integerOnly} />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Terms & Conditions" multiline rows={4} value={createForm.terms_and_conditions}
                      onChange={(e) => setCreateForm({ ...createForm, terms_and_conditions: e.target.value })}
                      helperText="Leave blank to use default terms. You can also generate with AI after creation." />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField fullWidth label="Special Clauses (Optional)" multiline rows={2} value={createForm.special_clauses}
                      onChange={(e) => setCreateForm({ ...createForm, special_clauses: e.target.value })} />
                  </Grid>
                </Grid>
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setCreateOpen(false); setSelectedBooking(null); }}>Cancel</Button>
          {selectedBooking && (
            <Button variant="contained" onClick={handleCreateAgreement}
              disabled={createLoading || !createForm.monthly_rent || !createForm.deposit_amount}
              startIcon={createLoading ? <CircularProgress size={16} /> : <NoteAdd />}>
              {createLoading ? 'Creating...' : 'Create Agreement'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Terminate Confirmation Dialog */}
      <Dialog open={!!terminateDialog} onClose={() => setTerminateDialog(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: 'error.main' }}>Terminate Tenancy Agreement</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2, mt: 1 }}>
            This action cannot be undone. Terminating the agreement will end the tenancy arrangement between all parties.
          </Alert>
          <TextField fullWidth multiline rows={3} value={terminateReason}
            onChange={(e) => setTerminateReason(e.target.value)}
            label="Reason for Termination" placeholder="Please provide a reason for terminating this agreement..."
            required />
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => { setTerminateDialog(null); setTerminateReason(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleTerminate}
            disabled={!terminateReason || actionLoading}>
            {actionLoading ? 'Terminating...' : 'Confirm Termination'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TenancyPage;
